import { Box, Icon, Tooltip, Typography } from '@mui/material'
import { CloseOutlined, InfoOutlined } from '@mui/icons-material'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { ColumnOrderListType } from '../../../redux/slices/entitySlice'
import EntityListSearch from '../../Entity/Components/EntityListSearch'
import ShipmentStatusList from './ShipmentStatusList'
import AlarmStatus from './AlarmStatus'
import DateRangeInput from '../../Common/DateRangeInput'

dayjs.extend(utc)

export interface InTransitFilterRowProps {
    templateId: string
    applicationId: string | undefined
    tabIndex: string | undefined
    triggerSearch: (searchText?: string) => void
    searchableColumns: ColumnOrderListType[]
    dateRange: [string | null, string | null]
    updateDateRange: (range: [string, string]) => void
    dateFilterLabel: string
    filtersCount: number
    clearAllFilters: () => void
    totalCount?: number
    totalCountWithoutFilters?: number
}

const InTransitFilterRow = ({
    templateId,
    applicationId,
    tabIndex,
    triggerSearch,
    searchableColumns,
    dateRange,
    updateDateRange,
    dateFilterLabel,
    filtersCount,
    clearAllFilters,
    totalCount,
    totalCountWithoutFilters,
}: InTransitFilterRowProps): ReactElement => {
    const { t } = useTranslation()

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'nowrap',
                padding: '8px 0px',
                minWidth: 0,
                overflowX: 'auto',
            }}
        >
            <Box>
                <EntityListSearch
                    tabIndex={tabIndex}
                    applicationId={applicationId}
                    templateId={templateId}
                    triggerSearch={triggerSearch}
                    searchableColumns={searchableColumns}
                />
            </Box>
            <Box
                sx={{
                    width: '170px',
                    background: 'white',
                    borderRadius: '5px',
                }}
            >
                <ShipmentStatusList />
            </Box>
            <Box
                sx={{
                    width: '150px',
                    background: 'white',
                    borderRadius: '5px',
                }}
            >
                <AlarmStatus />
            </Box>
            <Box sx={{ width: '255px' }}>
                <DateRangeInput
                    id="date-range-input-ccl"
                    label=""
                    placeholderLabel={t(dateFilterLabel)}
                    value={dateRange}
                    onChange={(_id: string, value: any) =>
                        updateDateRange(value)
                    }
                    clearable={true}
                    minDate={dayjs().subtract(180, 'day')}
                    maxDate={dayjs()}
                />
            </Box>
            {(filtersCount > 0 || totalCount !== undefined) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                    {filtersCount > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                background: '#00000042',
                                color: '#fff',
                                padding: '8px 15px 8px 12px',
                                borderRadius:
                                    totalCount !== undefined
                                        ? '25px 0 0 25px'
                                        : '25px',
                                gap: '6px',
                                cursor: 'pointer',
                                minWidth: '158px',
                                '&:hover': { background: '#00000048' },
                            }}
                            onClick={clearAllFilters}
                        >
                            <Box>
                                <Icon
                                    fontSize="small"
                                    id="clear-filters-button"
                                    aria-label={t('clearFilters')}
                                    title={t('clearFilters')}
                                >
                                    <CloseOutlined
                                        sx={{
                                            color: '#fff',
                                            marginTop: '1px',
                                        }}
                                    />
                                </Icon>
                            </Box>
                            <Box>
                                <Typography>{t('clearFilters')}</Typography>
                            </Box>
                            <Box
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    borderRadius: '25px',
                                    background: '#fff',
                                    color: '#000',
                                    marginLeft: '5px',
                                }}
                            >
                                <Typography>{filtersCount}</Typography>
                            </Box>
                        </Box>
                    )}
                    {totalCount !== undefined && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: '#00000029',
                                color: '#fff',
                                padding: '8px 14px',
                                borderRadius:
                                    filtersCount > 0 ? '0 25px 25px 0' : '25px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <Typography sx={{ fontSize: 14 }}>
                                {t('loadedShipemtsOFtotalShipments', {
                                    loadedShipemts: totalCount,
                                    totalShipments: totalCountWithoutFilters,
                                })}
                            </Typography>
                            <Tooltip
                                title={t(
                                    'shipmentCountExcludesReadyStateShipments',
                                )}
                                placement="bottom"
                            >
                                <InfoOutlined
                                    fontSize="small"
                                    sx={{ color: '#fff' }}
                                />
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    )
}

export default InTransitFilterRow
